import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, BriefcaseBusiness, FileDown, ChevronDown, ChevronRight } from "lucide-react";
import { ClienteCombobox } from "@/components/ClienteCombobox";
import { useRowHighlight } from "@/lib/useRowHighlight";

const TIPOS = ["visita","llamada","reunion","venta","cobranza","seguimiento","prospeccion","capacitacion","otro"];

const emptyActividad = () => ({
  _localId: `a-${Date.now()}-${Math.random().toString(36).slice(2,8)}`,
  tipo_actividad: "visita", cliente_id: "", cliente_nombre: "",
  lugar: "", hora_inicio: "", hora_fin: "", resultado: "", observaciones: "",
  proximo_seguimiento: "", genero_cotizacion: false, genero_pedido: false,
  hubo_venta: false, monto_estimado: 0,
});

export default function DailyReport() {
  const { highlightId, setRef, flash } = useRowHighlight();
  const [items, setItems] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // si !=null → edición individual de una actividad
  const [pdfPeriodo, setPdfPeriodo] = useState("semanal");
  const [expandedFechas, setExpandedFechas] = useState({});

  // Estado del "Nuevo reporte" (varias actividades)
  const [reporteForm, setReporteForm] = useState({
    fecha: new Date().toISOString().slice(0,10),
    observaciones_dia: "",
    actividades: [emptyActividad()],
  });

  const load = async () => {
    try {
      const [r, c] = await Promise.all([api.get("/sales/reporte-diario"), api.get("/masters/clientes")]);
      setItems(r.data); setClientes(c.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  // Agrupar actividades por fecha
  const grupos = useMemo(() => {
    const map = {};
    items.forEach(it => {
      const f = it.fecha || "—";
      if (!map[f]) map[f] = [];
      map[f].push(it);
    });
    return Object.keys(map).sort().reverse().map(f => ({
      fecha: f,
      actividades: map[f],
      total: map[f].reduce((s, a) => s + (a.monto_estimado || 0), 0),
      ventas: map[f].filter(a => a.hubo_venta).length,
    }));
  }, [items]);

  // Abrir nuevo reporte
  const abrirNuevoReporte = () => {
    setEditing(null);
    setReporteForm({
      fecha: new Date().toISOString().slice(0,10),
      observaciones_dia: "",
      actividades: [emptyActividad()],
    });
    setDialogOpen(true);
  };

  // Abrir edición individual
  const editarActividad = (it) => {
    setEditing(it);
    setReporteForm({
      fecha: it.fecha,
      observaciones_dia: "",
      actividades: [{ ...emptyActividad(), ...it, _localId: it.id }],
    });
    setDialogOpen(true);
  };

  const addActividad = () => setReporteForm(f => ({...f, actividades: [...f.actividades, emptyActividad()]}));
  const removeActividad = (idx) => setReporteForm(f => ({...f, actividades: f.actividades.filter((_, i) => i !== idx)}));
  const updateActividad = (idx, field, value) => setReporteForm(f => {
    const newA = [...f.actividades];
    newA[idx] = { ...newA[idx], [field]: value };
    if (field === "cliente_id") {
      const cli = clientes.find(c => c.id === value);
      newA[idx].cliente_nombre = cli?.razon_social || "";
    }
    return {...f, actividades: newA};
  });

  const guardarReporte = async () => {
    try {
      let savedIds = [];
      if (editing) {
        // Edición individual
        const act = reporteForm.actividades[0];
        const payload = { ...act, fecha: reporteForm.fecha };
        // eslint-disable-next-line no-unused-vars
        const { _localId, ...rest } = payload;
        const res = await api.patch(`/sales/reporte-diario/${editing.id}`, rest);
        savedIds = [res?.data?.id || editing.id];
        toast.success("Actividad actualizada");
      } else {
        // Nuevo reporte con N actividades
        const actividades = reporteForm.actividades.map(a => {
          // eslint-disable-next-line no-unused-vars
          const { _localId, ...rest } = a;
          return { ...rest, fecha: reporteForm.fecha };
        });
        // Validar al menos 1 con datos mínimos
        if (actividades.length === 0) return toast.error("Agregue al menos una actividad");
        const valid = actividades.every(a => a.tipo_actividad);
        if (!valid) return toast.error("Cada actividad necesita un tipo");
        const { data } = await api.post("/sales/reporte-diario/bulk", {
          fecha: reporteForm.fecha,
          observaciones_dia: reporteForm.observaciones_dia || null,
          actividades,
        });
        savedIds = (data.items || data.actividades || []).map(a => a.id).filter(Boolean);
        toast.success(`Reporte guardado: ${data.creadas} actividad(es)`);
      }
      setDialogOpen(false);
      setEditing(null);
      await load();
      // Resaltar la primera fila nueva
      if (savedIds[0]) flash(savedIds[0]);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeUna = async (it) => {
    if (!window.confirm("¿Eliminar esta actividad?")) return;
    try { await api.delete(`/sales/reporte-diario/${it.id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const descargarPDF = async () => {
    try {
      const resp = await api.get(`/sales/reporte-diario/pdf?periodo=${pdfPeriodo}`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reporte_actividades_${pdfPeriodo}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Reporte ${pdfPeriodo} descargado`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleFecha = (f) => setExpandedFechas(prev => ({ ...prev, [f]: !prev[f] }));

  return (
    <div className="space-y-6" data-testid="daily-report-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Ventas</div>
          <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Reporte diario</h1>
          <p className="text-sm text-[#4a5c53] mt-1">
            Al final del día, cree un único reporte con todas las actividades que realizó (visitas, llamadas, reuniones, ventas, cobranzas, etc.).
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={pdfPeriodo} onValueChange={setPdfPeriodo}>
            <SelectTrigger className="w-32 h-9 text-xs" data-testid="pdf-periodo-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="diario">Diario</SelectItem>
              <SelectItem value="semanal">Semanal</SelectItem>
              <SelectItem value="mensual">Mensual</SelectItem>
              <SelectItem value="anual">Anual</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={descargarPDF} variant="outline" size="sm" data-testid="activity-pdf-btn">
            <FileDown size={14} className="mr-1.5" /> PDF
          </Button>
          <Button onClick={abrirNuevoReporte} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="create-report-btn">
            <Plus size={16} className="mr-2" /> Nuevo reporte
          </Button>
        </div>
      </div>

      {/* Lista agrupada por fecha */}
      <div className="space-y-3">
        {grupos.length === 0 && (
          <div className="rounded-md border border-dashed border-[#d1dbd6] bg-white p-12 text-center text-[#8ba397]" data-testid="empty-state">
            <BriefcaseBusiness size={36} className="inline mb-3" /><br />
            Aún no hay reportes registrados.<br />
            <span className="text-sm">Toque "Nuevo reporte" al final del día para registrar todas sus actividades.</span>
          </div>
        )}
        {grupos.map((g) => {
          const expanded = expandedFechas[g.fecha] ?? true; // expandido por defecto
          return (
            <div key={g.fecha} className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden" data-testid={`grupo-${g.fecha}`}>
              <button
                type="button"
                onClick={() => toggleFecha(g.fecha)}
                className="w-full px-4 py-3 bg-[#f7f9f8] border-b border-[#d1dbd6] flex items-center justify-between gap-3 hover:bg-[#eef2f0] transition-colors"
              >
                <div className="flex items-center gap-3">
                  {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <div className="text-left">
                    <div className="font-bold text-[#15241d]">Reporte del {g.fecha}</div>
                    <div className="text-xs text-[#5c7368]">
                      {g.actividades.length} actividad(es) · {g.ventas} con venta · Monto estimado: ${g.total.toFixed(2)}
                    </div>
                  </div>
                </div>
                <Badge variant="outline" className="text-[10px]">{g.actividades[0]?.vendedor_nombre || "—"}</Badge>
              </button>
              {expanded && (
                <table className="erp-table">
                  <thead>
                    <tr>
                      <th className="w-32">Tipo</th>
                      <th>Cliente</th>
                      <th>Resultado</th>
                      <th className="w-24 text-center">Hora</th>
                      <th className="w-16">Cot.</th>
                      <th className="w-16">Ped.</th>
                      <th className="w-20">Venta</th>
                      <th className="w-24 text-right">Monto</th>
                      <th className="w-24"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.actividades.map(it => (
                      <tr key={it.id} ref={setRef(it.id)} className={highlightId === it.id ? "row-highlight" : ""} data-testid={`activity-row-${it.id}`}>
                        <td><Badge variant="outline" className="capitalize">{it.tipo_actividad}</Badge></td>
                        <td className="font-medium">{it.cliente_nombre || "—"}</td>
                        <td className="text-xs text-[#4a5c53] max-w-md truncate">{it.resultado || "—"}</td>
                        <td className="text-center text-xs text-[#5c7368]">{it.hora_inicio || "—"}</td>
                        <td className="text-center">{it.genero_cotizacion ? "✓" : ""}</td>
                        <td className="text-center">{it.genero_pedido ? "✓" : ""}</td>
                        <td>{it.hubo_venta ? <Badge className="fefo-safe border">Sí</Badge> : ""}</td>
                        <td className="text-right tabular-nums">${(it.monto_estimado||0).toFixed(2)}</td>
                        <td>
                          <div className="flex gap-1 justify-end">
                            <Button size="icon" variant="ghost" onClick={() => editarActividad(it)} data-testid={`edit-activity-${it.id}`}><Edit2 size={16} /></Button>
                            <Button size="icon" variant="ghost" onClick={() => removeUna(it)}><Trash2 size={16} className="text-[#b53a25]" /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>

      {/* Dialog Nuevo reporte / Editar */}
      <Sheet open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) setEditing(null); }}>
        <SheetContent side="right" className="w-full sm:!max-w-[1100px] flex flex-col p-0">
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#d1dbd6]">
            <SheetTitle>{editing ? "Editar actividad" : "Nuevo reporte del día"}</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4">

          {/* Cabecera: fecha + observaciones del día */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Fecha del reporte</Label>
              <Input type="date" value={reporteForm.fecha} onChange={(e) => setReporteForm({...reporteForm, fecha: e.target.value})} data-testid="report-fecha" />
            </div>
            {!editing && (
              <div className="md:col-span-2">
                <Label>Observación general del día (opcional)</Label>
                <Input
                  value={reporteForm.observaciones_dia}
                  onChange={(e) => setReporteForm({...reporteForm, observaciones_dia: e.target.value})}
                  placeholder="Ej: Visitamos zona norte, clima dificultó traslados…"
                  data-testid="report-obs-dia"
                />
              </div>
            )}
          </div>

          {/* Lista de actividades */}
          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">
                Actividades del día ({reporteForm.actividades.length})
              </div>
              {!editing && (
                <Button size="sm" onClick={addActividad} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="add-activity-btn">
                  <Plus size={14} className="mr-1" /> Agregar actividad
                </Button>
              )}
            </div>

            {reporteForm.actividades.map((a, idx) => (
              <ActividadCard
                key={a._localId || idx}
                actividad={a}
                idx={idx}
                clientes={clientes}
                onChange={updateActividad}
                onRemove={removeActividad}
                canRemove={!editing && reporteForm.actividades.length > 1}
              />
            ))}
          </div>

          </div>
          <SheetFooter className="border-t border-[#d1dbd6] px-6 py-4 flex-col-reverse sm:flex-row gap-2 sm:gap-0">
            <Button variant="outline" className="h-11 sm:h-9 w-full sm:w-auto" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={guardarReporte} className="bg-[#205c45] hover:bg-[#1a4a37] h-11 sm:h-9 w-full sm:w-auto" data-testid="save-report-btn">
              {editing ? "Guardar cambios" : `Guardar reporte (${reporteForm.actividades.length})`}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}

/** Tarjeta para cada actividad dentro del reporte del día. */
function ActividadCard({ actividad: a, idx: i, clientes, onChange, onRemove, canRemove }) {
  const [expanded, setExpanded] = useState(true);
  return (
    <div className="rounded-md border border-[#d1dbd6] bg-white p-3 space-y-2" data-testid={`activity-card-${i}`}>
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="text-xs font-bold uppercase tracking-wider text-[#205c45] flex items-center gap-1"
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          Actividad {i + 1}
          <Badge variant="outline" className="ml-2 capitalize text-[10px]">{a.tipo_actividad}</Badge>
          {a.cliente_nombre && <span className="ml-1 text-[11px] text-[#5c7368] normal-case font-normal">· {a.cliente_nombre}</span>}
        </button>
        {canRemove && (
          <Button size="sm" variant="ghost" onClick={() => onRemove(i)} className="h-8 text-[#b53a25]" data-testid={`remove-activity-${i}`}>
            <Trash2 size={14} className="mr-1" /> Quitar
          </Button>
        )}
      </div>
      {expanded && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Tipo de actividad</Label>
            <Select value={a.tipo_actividad} onValueChange={(v) => onChange(i, "tipo_actividad", v)}>
              <SelectTrigger className="h-10" data-testid={`activity-tipo-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIPOS.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Cliente</Label>
            <ClienteCombobox
              value={a.cliente_id || ""}
              onChange={(v) => onChange(i, "cliente_id", v)}
              clientes={clientes}
              placeholder="Buscar cliente…"
              testId={`activity-client-${i}`}
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Lugar / Dirección</Label>
            <Input value={a.lugar} onChange={(e) => onChange(i, "lugar", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Hora inicio</Label>
              <Input type="time" value={a.hora_inicio} onChange={(e) => onChange(i, "hora_inicio", e.target.value)} />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Hora fin</Label>
              <Input type="time" value={a.hora_fin} onChange={(e) => onChange(i, "hora_fin", e.target.value)} />
            </div>
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Resultado</Label>
            <Input value={a.resultado} onChange={(e) => onChange(i, "resultado", e.target.value)} data-testid={`activity-result-${i}`} placeholder="Ej: Cliente pidió cotización, contactar en 3 días" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Observaciones</Label>
            <Textarea rows={2} value={a.observaciones} onChange={(e) => onChange(i, "observaciones", e.target.value)} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Próximo seguimiento</Label>
            <Input type="date" value={a.proximo_seguimiento} onChange={(e) => onChange(i, "proximo_seguimiento", e.target.value)} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Monto estimado / vendido</Label>
            <Input type="number" inputMode="decimal" step="0.01" value={a.monto_estimado} onChange={(e) => onChange(i, "monto_estimado", parseFloat(e.target.value) || 0)} />
          </div>
          <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-2 pt-2">
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={a.genero_cotizacion} onCheckedChange={(v) => onChange(i, "genero_cotizacion", !!v)} />Generó cotización</label>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={a.genero_pedido} onCheckedChange={(v) => onChange(i, "genero_pedido", !!v)} />Generó pedido</label>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={a.hubo_venta} onCheckedChange={(v) => onChange(i, "hubo_venta", !!v)} />Hubo venta</label>
          </div>
        </div>
      )}
    </div>
  );
}
